/**
 * File: brownfieldConversionService.ts
 * Description: Service for SAHLD-to-Archie brownfield conversion API calls
 * Author: System
 * Created: 2026-03-02
 * Last Modified: 2026-03-02
 */

import { API_CONFIG } from '../config/api';
import { ADRSuggestion } from '../types/adr';

import { clearAccessToken, getAccessToken } from './accessTokenManager';

const BASE_URL = API_CONFIG.BASE_URL;

// ---- HTTP status constants ------------------------------------------------
const HTTP_STATUS_NOT_FOUND = 404;
const HTTP_STATUS_UNAUTHORIZED = 401;

// ---- SSE parsing constants ------------------------------------------------
const SSE_DATA_PREFIX = 'data: ';
const SSE_DATA_PREFIX_LENGTH = SSE_DATA_PREFIX.length;

// ---- Types ----------------------------------------------------------------

export interface ConversionSection {
  /** Machine key matching ArchitectureSection enum value. */
  name: string;
  /** Human-readable label. */
  label: string;
  /** Whether the section has been converted and saved. */
  converted: boolean;
  /** Archie-format markdown content (empty string if not yet converted). */
  content: string;
}

export interface ConversionResult {
  project_id: number;
  sections: ConversionSection[];
  is_complete: boolean;
}

// SSE event shapes emitted by the backend stream endpoint.
export type ConversionStreamEvent =
  | { type: 'section_start'; section: string; label: string; index: number; total: number }
  | {
      type: 'section_complete';
      section: string;
      label: string;
      index: number;
      total: number;
      content: string;
    }
  | { type: 'complete'; total: number }
  | { type: 'error'; error: string };

// ---- Helpers ---------------------------------------------------------------

function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ---- API calls -------------------------------------------------------------

/**
 * Fetch any already-persisted conversion results for a project.
 *
 * @param projectId - Numeric project ID.
 * @returns Persisted conversion data, or null if nothing has been converted.
 */
export async function getConversionResult(
  projectId: number,
): Promise<ConversionResult | null> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${BASE_URL}/api/project/${projectId}/brownfield-convert`,
    { headers },
  );

  if (response.status === HTTP_STATUS_NOT_FOUND) { return null; }
  if (!response.ok) {
    throw new Error(
      `Failed to fetch conversion result: ${response.statusText}`,
    );
  }
  return response.json() as Promise<ConversionResult>;
}

/**
 * Parse raw SSE buffer lines into typed ConversionStreamEvent objects.
 * Lines not starting with the SSE data prefix are silently ignored.
 */
function parseSSEBufferLines(lines: string[]): ConversionStreamEvent[] {
  const events: ConversionStreamEvent[] = [];
  for (const line of lines) {
    if (!line.startsWith(SSE_DATA_PREFIX)) { continue; }
    const raw = line.slice(SSE_DATA_PREFIX_LENGTH).trim();
    if (!raw) { continue; }
    try {
      events.push(JSON.parse(raw) as ConversionStreamEvent);
    } catch {
      // Malformed JSON — skip.
    }
  }
  return events;
}

/**
 * Stream the SAHLD-to-Archie conversion, yielding typed events for each
 * section as it completes.
 *
 * @param projectId - Numeric project ID.
 * @param sections - Optional list of section names to convert. When omitted
 *   all sections are converted. Use this for per-section refresh.
 * @yields Typed ``ConversionStreamEvent`` objects.
 */
export async function* streamConversion(
  projectId: number,
  sections?: string[],
): AsyncGenerator<ConversionStreamEvent> {
  const url = new URL(
    `${BASE_URL}/api/project/${projectId}/brownfield-convert/stream`,
  );
  if (sections && sections.length > 0) {
    url.searchParams.set('sections', sections.join(','));
  }

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (response.status === HTTP_STATUS_UNAUTHORIZED) {
    clearAccessToken();
    globalThis.location.href = '/login';
    return;
  }
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) { throw new Error('Failed to get response reader'); }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) { break; }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      // Keep last (potentially incomplete) line in buffer.
      buffer = lines.pop() ?? '';

      for (const event of parseSSEBufferLines(lines)) {
        yield event;
      }
    }
  } finally {
    reader.cancel();
  }
}
/**
 * Persist manually-edited content for a single conversion section.
 *
 * @param projectId - Numeric project ID.
 * @param sectionName - Section value key, e.g. ``adr_summary``.
 * @param content - Updated markdown content.
 */
export async function updateSection(
  projectId: number,
  sectionName: string,
  content: string,
): Promise<void> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${BASE_URL}/api/project/${projectId}/brownfield-convert/${sectionName}`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ content }),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to save section '${sectionName}': ${response.statusText}`,
    );
  }
}

// ---- Brownfield ADR helpers ------------------------------------------------

const ADR_SECTION_NAME = 'adr_summary';
const BROWNFIELD_CONFIDENCE = 100;
/** Maximum characters kept for ADR description text. */
const MAX_DESCRIPTION_CHARS = 300;
// Regex that matches a standalone code-fence line (``` optionally followed by a language tag)
const CODE_FENCE_LINE_RE = /^```\S*$/;

/**
 * Remove code-fence lines that the LLM adds around ADR blocks.
 *
 * The conversion prompt template shows each ADR wrapped in ``` fences as an
 * example, so the model often emits each block inside its own fence.  These
 * fences carry no semantic meaning for ADR content (which is only prose and
 * block-quotes), so it is safe to strip ALL standalone fence lines.
 *
 * A "standalone" fence line is one whose trimmed content matches ``` (with an
 * optional language tag but nothing else on that line).  Real code blocks
 * embedded inside prose would still be removed, but ADR sections never
 * contain them in practice.
 */
function removeFenceLines(markdown: string): string {
  return markdown
    .split('\n')
    .filter((l) => !CODE_FENCE_LINE_RE.test(l.trim()))
    .join('\n');
}

/**
 * Parse the ADR summary markdown section produced by the brownfield conversion
 * into a list of {@link ADRSuggestion} objects ready for the ADRs page.
 *
 * The expected format (from config.brownfield.yml) is:
 * ```
 * ## ADR-N: Short title
 *
 * **Decision (verbatim):**
 * > Verbatim text …
 * ```
 *
 * @param markdown - Raw markdown content of the `adr_summary` section.
 * @param baseId - Optional prefix used for generated IDs.
 * @returns Array of parsed ADRSuggestion objects.
 */
/** Maximum characters used as a synthetic title from raw quoted text. */
const MAX_TITLE_CHARS = 80;

/**
 * Build a single ADRSuggestion from a block of text lines.
 * The first line is the heading; subsequent lines are scanned for block-quotes.
 * Falls back to plain prose when no block-quotes are present.
 */
function buildSuggestion(
  lines: string[],
  index: number,
  baseId: string,
  heading: string,
  titleText: string,
): ADRSuggestion {
  const quotedLines = lines
    .filter((l) => l.trimStart().startsWith('>'))
    .map((l) => l.replace(/^>\s?/, '').trim())
    .filter(Boolean);

  const description =
    quotedLines.join(' ') ||
    lines
      .slice(heading ? 1 : 0)
      .join(' ')
      .replace(/\*\*[^*]+\*\*/g, '')
      .trim()
      .slice(0, MAX_DESCRIPTION_CHARS);

  return {
    id: `${baseId}-adr-${index + 1}-${Date.now()}`,
    title: titleText,
    description,
    confidenceScore: BROWNFIELD_CONFIDENCE,
    contextSource: 'SAHLD Brownfield Document',
    reasoning:
      'Extracted verbatim from the existing SAHLD architecture document.',
  };
}

/**
 * Fallback: parse content that has no ``## ADR-N`` headings.
 *
 * The LLM occasionally returns a flat list of block-quoted sentences with no
 * section headings.  Each non-empty paragraph (separated by blank lines) is
 * treated as a distinct architectural decision.  The first 80 characters of
 * the quoted text become the synthetic title.
 */
function parseFlatQuoteList(
  cleaned: string,
  baseId: string,
): ADRSuggestion[] {
  const suggestions: ADRSuggestion[] = [];
  // Split on one-or-more blank lines to get paragraphs.
  const paragraphs = cleaned.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);

  paragraphs.forEach((para, index) => {
    const lines = para.split('\n');
    // Extract all quoted text from the paragraph.
    const quotedText = lines
      .filter((l) => l.trimStart().startsWith('>'))
      .map((l) => l.replace(/^>\s?/, '').trim())
      .filter(Boolean)
      .join(' ');

    const rawText = quotedText || lines.join(' ').replace(/\*\*[^*]+\*\*/g, '').trim();
    if (!rawText) { return; }

    // Synthesise a short title from the first sentence or first N chars.
    const firstSentence = rawText.split(/[.!?]/)[0]?.trim() ?? rawText;
    const title =
      firstSentence.length <= MAX_TITLE_CHARS
        ? firstSentence
        : `${rawText.slice(0, MAX_TITLE_CHARS)}…`;

    suggestions.push({
      id: `${baseId}-adr-${index + 1}-${Date.now()}`,
      title,
      description: rawText.slice(0, MAX_DESCRIPTION_CHARS),
      confidenceScore: BROWNFIELD_CONFIDENCE,
      contextSource: 'SAHLD Brownfield Document',
      reasoning:
        'Extracted verbatim from the existing SAHLD architecture document.',
    });
  });

  return suggestions;
}

export function parseBrownfieldAdrMarkdown(
  markdown: string,
  baseId = 'brownfield',
): ADRSuggestion[] {
  const suggestions: ADRSuggestion[] = [];
  // Remove all standalone code-fence lines the LLM may have added around blocks
  const cleaned = removeFenceLines(markdown);

  // ---- Structured path: content has "## ADR-N:" headings -------------------
  // Split on headings of the form "## ADR-N:", "## ADR N:" or "## ADRN:"
  // The [-\s]* makes the separator between "ADR" and the number optional and
  // tolerates both dashes and spaces that LLMs sometimes emit instead.
  const hasAdrHeadings = /^##\s+ADR[-\s]*/m.test(cleaned);

  if (!hasAdrHeadings) {
    // ---- Flat fallback: no headings found, parse paragraph-by-paragraph ----
    return parseFlatQuoteList(cleaned, baseId);
  }

  const blocks = cleaned.split(/^##\s+ADR[-\s]*/m).filter(Boolean);

  blocks.forEach((block, index) => {
    const lines = block.trim().split('\n');
    const heading = lines[0] ?? '';

    // Strip leading "N: ", "N-", or "N " to get the title
    const title = heading.replace(/^\d+[-:\s]+/, '').trim();
    if (!title) { return; }

    suggestions.push(buildSuggestion(lines, index, baseId, heading, title));
  });

  return suggestions;
}

/**
 * Normalise section content to a plain markdown string.
 * The backend may return a plain string or a `{text: "..."}` object.
 */
function extractMarkdownString(raw: unknown): string {
  if (typeof raw === 'string') { return raw; }
  if (typeof raw === 'object' && raw !== null && 'text' in raw) {
    const textValue = (raw as { text: unknown }).text;
    return typeof textValue === 'string' ? textValue : '';
  }
  return '';
}

/**
 * Fetch and parse brownfield ADR suggestions for a project.
 *
 * Returns an empty array when the project has no conversion results or when
 * the `adr_summary` section is not yet converted.
 *
 * @param projectId - Numeric project ID.
 * @returns Parsed ADR suggestions from the SAHLD document.
 */
export async function getBrownfieldAdrSuggestions(
  projectId: number,
): Promise<ADRSuggestion[]> {
  const result = await getConversionResult(projectId).catch(() => null);
  if (!result) { return []; }

  const adrSection = result.sections.find(
    (s) => s.name === ADR_SECTION_NAME && s.converted,
  );
  if (!adrSection?.content) { return []; }

  // The backend may return content as a plain string (brownfield conversion)
  // or as a {text: "..."} dict (when the agent pipeline wrote the section).
  // Normalise to a plain string before parsing.
  const markdown = extractMarkdownString(adrSection.content as unknown);

  return parseBrownfieldAdrMarkdown(markdown);
}